import type { JWPlayer } from '../../../types/jwplayer';

export type CaptionTrack = {
  id?: string | number;
  label?: string;
  language?: string;
};

export type AudioTrack = {
  name?: string;
  label?: string;
  language?: string;
  hlsjsIndex?: number;
  defaulttrack?: boolean;
  autoselect?: boolean;
  groupid?: string;
  uri?: string;
  duration?: number;
};

type HlsJsLike = {
  audioTrack?: number;
  audioTracks?: Array<{ name?: string; lang?: string; groupId?: string; id?: number }>;
};

type ExtendedAudioPlayer = CaptionsCapablePlayer & {
  hls?: HlsJsLike;
  getProvider?: () => { name?: string; hls?: HlsJsLike; hlsjs?: HlsJsLike } | string;
};

const normalizeTrackLabel = (track?: AudioTrack) => (track?.name || track?.label || '').trim().toLowerCase();

export const resolveLiveAudioTrackIndex = (
  player: CaptionsCapablePlayer,
  selected: string,
  tracksOverride?: AudioTrack[],
) => {
  const liveTracks = player.getAudioTracks?.() ?? [];
  const referenceTracks = liveTracks.length ? liveTracks : tracksOverride ?? [];
  if (!referenceTracks.length) {
    return -1;
  }

  const parsedIndex = parseAudioTrackSelectionIndex(selected);
  const referenceIndex = findAudioTrackIndex(referenceTracks, selected);
  const target = referenceTracks[referenceIndex >= 0 ? referenceIndex : parsedIndex ?? -1];

  if (!liveTracks.length) {
    return referenceIndex >= 0 ? referenceIndex : parsedIndex ?? -1;
  }

  if (parsedIndex != null && parsedIndex >= 0 && parsedIndex < liveTracks.length) {
    const parsedTarget = referenceTracks[parsedIndex];
    const liveByParsed = liveTracks.findIndex(
      (track) =>
        normalizeTrackLabel(track) === normalizeTrackLabel(parsedTarget) ||
        trackLanguage(track) === trackLanguage(parsedTarget),
    );
    if (liveByParsed >= 0) {
      return liveByParsed;
    }
  }

  if (target) {
    const byName = liveTracks.findIndex((track) => normalizeTrackLabel(track) === normalizeTrackLabel(target));
    if (byName >= 0) {
      return byName;
    }

    const targetLanguage = trackLanguage(target);
    const languageMatches = liveTracks
      .map((track, index) => ({ track, index }))
      .filter(({ track }) => trackLanguage(track) === targetLanguage);

    if (languageMatches.length === 1) {
      return languageMatches[0].index;
    }

    if (parsedIndex != null && languageMatches[parsedIndex]) {
      return languageMatches[parsedIndex].index;
    }
  }

  if (parsedIndex != null && parsedIndex < liveTracks.length) {
    return parsedIndex;
  }

  return referenceIndex >= 0 ? Math.min(referenceIndex, liveTracks.length - 1) : -1;
};

export type CaptionsCapablePlayer = JWPlayer & {
  getCaptionsList?: () => CaptionTrack[];
  getCurrentCaptions?: () => number;
  setCurrentCaptions?: (index: number) => void;
  getAudioTracks?: () => AudioTrack[];
  getCurrentAudioTrack?: () => number;
  setCurrentAudioTrack?: (index: number) => void;
  getContainer?: () => HTMLElement | null;
};

const CAPTIONS_STORAGE_KEY = 'videoCaptions';
export const AUDIO_TRACK_SELECTION_PREFIX = 'audio:';

const LANGUAGE_ALIASES: Record<string, string> = {
  en: 'en',
  eng: 'en',
  english: 'en',
  es: 'es',
  spa: 'es',
  spanish: 'es',
  espanol: 'es',
  español: 'es',
  castellano: 'es',
  fr: 'fr',
  fra: 'fr',
  fre: 'fr',
  french: 'fr',
  français: 'fr',
  francais: 'fr',
  de: 'de',
  deu: 'de',
  ger: 'de',
  german: 'de',
  deutsch: 'de',
  ar: 'ar',
  ara: 'ar',
  arabic: 'ar',
  arab: 'ar',
  ta: 'ta',
  tam: 'ta',
  tamil: 'ta',
  hi: 'hi',
  hin: 'hi',
  hindi: 'hi',
};

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  ar: 'Arabic',
  ta: 'Tamil',
  hi: 'Hindi',
};

export const canonicalLanguage = (value?: string) => {
  if (!value) {
    return '';
  }

  const lower = value.toLowerCase().trim();
  if (lower === 'off') {
    return 'off';
  }

  const primary = lower.split(/[-_]/)[0];
  return LANGUAGE_ALIASES[lower] || LANGUAGE_ALIASES[primary] || primary;
};

const labelLanguage = (label?: string) => {
  const value = (label || '').toLowerCase();
  if (!value) {
    return '';
  }

  if (value === 'off') {
    return 'off';
  }

  if (value.includes('spanish') || value.includes('español') || value.includes('espanol') || value.includes('castellano')) {
    return 'es';
  }

  if (value.includes('english')) {
    return 'en';
  }

  if (value.includes('french') || value.includes('français') || value.includes('francais')) {
    return 'fr';
  }

  if (value.includes('tamil') || value.includes('தமிழ்')) {
    return 'ta';
  }

  if (value.includes('hindi') || value.includes('हिन्दी') || value.includes('हिंदी')) {
    return 'hi';
  }

  if (value.includes('german') || value.includes('deutsch')) {
    return 'de';
  }

  if (value.includes('arabic') || value.includes('العربية')) {
    return 'ar';
  }

  return canonicalLanguage(value);
};

export const trackLanguage = (track: CaptionTrack | AudioTrack) => {
  const id = 'id' in track ? String(track.id ?? '').toLowerCase() : '';
  const label = 'label' in track ? track.label : undefined;
  const name = 'name' in track ? track.name : undefined;
  const displayName = label || name || '';

  if (id === 'off' || displayName.toLowerCase() === 'off') {
    return 'off';
  }

  return canonicalLanguage(track.language) || labelLanguage(displayName);
};

export const isOffCaptionTrack = (track: CaptionTrack) => trackLanguage(track) === 'off';

export const normalizeCaptionList = (tracks: CaptionTrack[]) => {
  if (!tracks.length) {
    return tracks;
  }

  if (isOffCaptionTrack(tracks[0])) {
    return tracks;
  }

  return [{ id: 'off', label: 'Off' }, ...tracks];
};

export const parseCaptionSelection = (selected: string) => {
  if (!selected || selected === 'off') {
    return {
      language: 'off',
      ordinal: undefined as number | undefined,
      jwIndex: undefined as number | undefined,
      source: 'off' as const,
    };
  }

  if (selected.startsWith('embedded:')) {
    const jwIndex = Number(selected.slice('embedded:'.length));
    return {
      language: 'embedded',
      ordinal: undefined as number | undefined,
      jwIndex: Number.isFinite(jwIndex) ? jwIndex : undefined,
      source: 'embedded' as const,
    };
  }

  const separator = selected.lastIndexOf(':');
  if (separator > 0 && /^\d+$/.test(selected.slice(separator + 1))) {
    return {
      language: canonicalLanguage(selected.slice(0, separator)),
      ordinal: Number(selected.slice(separator + 1)),
      jwIndex: undefined as number | undefined,
      source: 'sidecar' as const,
    };
  }

  return {
    language: canonicalLanguage(selected),
    ordinal: undefined as number | undefined,
    jwIndex: undefined as number | undefined,
    source: 'sidecar' as const,
  };
};

export const findCaptionIndex = (tracks: CaptionTrack[], selected: string) => {
  const { language: selectedLanguage, ordinal } = parseCaptionSelection(selected);
  const list = normalizeCaptionList(tracks);

  if (selectedLanguage === 'off') {
    const offIndex = list.findIndex(isOffCaptionTrack);
    return offIndex >= 0 ? offIndex : 0;
  }

  const matches = list
    .map((track, index) => ({ track, index }))
    .filter(({ track }) => {
      const language = trackLanguage(track);
      return language !== 'off' && language === selectedLanguage;
    });

  if (ordinal != null && matches[ordinal]) {
    return matches[ordinal].index;
  }

  if (matches.length === 1 || (matches.length > 1 && ordinal == null)) {
    return matches[0].index;
  }

  return -1;
};

export const findAudioTrackIndex = (tracks: AudioTrack[], selected: string) => {
  if (!selected || !tracks.length) {
    return -1;
  }

  const indexFromId = parseAudioTrackSelectionIndex(selected);
  if (indexFromId != null) {
    return indexFromId < tracks.length ? indexFromId : -1;
  }

  const { language: selectedLanguage, ordinal } = parseCaptionSelection(selected);
  const selectedLower = selected.toLowerCase();

  const exactIndex = tracks.findIndex((track) => {
    const language = (track.language || '').toLowerCase().replace('_', '-');
    return language === selectedLower;
  });

  if (exactIndex >= 0 && ordinal == null) {
    return exactIndex;
  }

  const matches = tracks
    .map((track, index) => ({ track, index }))
    .filter(({ track }) => trackLanguage(track) === selectedLanguage);

  if (ordinal != null && matches[ordinal]) {
    return matches[ordinal].index;
  }

  if (matches.length === 1 || (matches.length > 1 && ordinal == null)) {
    return matches[0].index;
  }

  return -1;
};

export const toAudioTrackSelectionId = (index: number) => `${AUDIO_TRACK_SELECTION_PREFIX}${index}`;

export const parseAudioTrackSelectionIndex = (selected: string) => {
  if (!selected.startsWith(AUDIO_TRACK_SELECTION_PREFIX)) {
    return undefined;
  }

  const index = Number(selected.slice(AUDIO_TRACK_SELECTION_PREFIX.length));
  return Number.isInteger(index) && index >= 0 ? index : undefined;
};

export const getStoredCaptions = () => 'off';

export const persistCaptionsPreference = (value: string) => {
  try {
    localStorage.setItem(CAPTIONS_STORAGE_KEY, value);
  } catch {
    // Ignore storage access errors (private mode, etc.)
  }
};

export const getPlayerVideo = (player?: CaptionsCapablePlayer, root?: HTMLElement | null) =>
  (player?.getContainer?.()?.querySelector('video') ||
    root?.querySelector('video') ||
    document.querySelector('.jwplayer video')) as HTMLVideoElement | null | undefined;

const isCaptionTextTrack = (track: TextTrack) =>
  track.kind === 'captions' || track.kind === 'subtitles' || track.kind === '' || track.kind === 'forced';

export const captionLanguageName = (language?: string, fallback?: string) => {
  const canonical = canonicalLanguage(language);
  return fallback || LANGUAGE_NAMES[canonical] || language || '';
};

const matchTextTrack = (tracks: TextTrack[], selected: string) => {
  const { language: selectedLanguage, ordinal } = parseCaptionSelection(selected);
  if (selectedLanguage === 'off') {
    return undefined;
  }

  const matches = tracks.filter(
    (track) => canonicalLanguage(track.language) === selectedLanguage || labelLanguage(track.label) === selectedLanguage,
  );

  if (ordinal != null && matches[ordinal]) {
    return matches[ordinal];
  }

  return matches[0];
};

export const applyHtml5TextTracks = (player: CaptionsCapablePlayer | undefined, selected: string, root?: HTMLElement | null) => {
  const video = getPlayerVideo(player, root);
  if (!video?.textTracks?.length) {
    return;
  }

  const tracks = Array.from(video.textTracks).filter(isCaptionTextTrack);
  const matched = matchTextTrack(tracks, selected);

  tracks.forEach((track) => {
    track.mode = track === matched ? 'showing' : 'hidden';
  });
};

const stripVttMarkup = (value: string) =>
  value
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();

const cueTextAtTime = (track: TextTrack, time: number) => {
  const lines: string[] = [];
  const appendCue = (cue: TextTrackCue) => {
    const text = stripVttMarkup((cue as VTTCue).text || '');
    if (text) {
      lines.push(text);
    }
  };

  if (track.activeCues?.length) {
    Array.from(track.activeCues).forEach(appendCue);
    if (lines.length) {
      return lines.join('\n');
    }
  }

  const allCues = track.cues;
  if (!allCues?.length) {
    return '';
  }

  for (let index = 0; index < allCues.length; index += 1) {
    const cue = allCues[index];
    if (time >= cue.startTime && time <= cue.endTime) {
      appendCue(cue);
    }
  }

  return lines.join('\n');
};

const uniqueCaptionLines = (value: string) => {
  const lines = value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return Array.from(new Set(lines)).join('\n');
};

const isFinitePlaybackTime = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export const getActiveCaptionText = (
  player?: CaptionsCapablePlayer,
  time?: number,
  root?: HTMLElement | null,
  selected?: string,
) => {
  if (!player) {
    return '';
  }

  const video = getPlayerVideo(player, root);
  const position = isFinitePlaybackTime(time) ? time : video?.currentTime ?? 0;

  if (video?.textTracks?.length) {
    const tracks = Array.from(video.textTracks).filter(isCaptionTextTrack);
    const matched = selected ? matchTextTrack(tracks, selected) : undefined;
    const orderedTracks = matched
      ? [matched, ...tracks.filter((track) => track !== matched)]
      : [...tracks.filter((track) => track.mode === 'showing'), ...tracks.filter((track) => track.mode !== 'showing')];

    for (const track of orderedTracks) {
      const previousMode = track.mode;
      if (!track.cues && previousMode === 'disabled') {
        track.mode = 'hidden';
      }

      try {
        const text = uniqueCaptionLines(cueTextAtTime(track, position));
        if (text) {
          return text;
        }
      } finally {
        if (previousMode === 'disabled') {
          track.mode = previousMode;
        }
      }
    }
  }

  const roots = [player.getContainer?.(), root, typeof document !== 'undefined' ? document : null].filter(Boolean) as ParentNode[];

  for (const nodeRoot of roots) {
    const nodes = nodeRoot.querySelectorAll('.jw-captions-text, .jw-text-track-cue, .jw-captions.jw-captions-enabled');
    const text = uniqueCaptionLines(
      Array.from(nodes)
        .map((node) => stripVttMarkup(node.textContent || ''))
        .filter(Boolean)
        .join('\n'),
    );

    if (text) {
      return text;
    }
  }

  return '';
};

export const applyCaptionsToPlayer = (
  player: CaptionsCapablePlayer | undefined,
  selected: string,
  tracksOverride?: CaptionTrack[],
  root?: HTMLElement | null,
) => {
  if (!player) {
    return;
  }

  const setCaptions = player.setCurrentCaptions;
  const parsed = parseCaptionSelection(selected);

  if (parsed.source === 'embedded' && parsed.jwIndex != null && setCaptions) {
    setCaptions(parsed.jwIndex);
    applyHtml5TextTracks(player, selected, root);
    return;
  }

  applyHtml5TextTracks(player, selected, root);

  if (!setCaptions) {
    return;
  }

  // Sidecar / Off: keep JW captions off so overlay text comes from VTT/SRT cues.
  if (parsed.source === 'off' || parsed.source === 'sidecar') {
    setCaptions(0);
    applyHtml5TextTracks(player, selected, root);
    return;
  }

  const fromPlayer = player.getCaptionsList?.() ?? [];
  const playerHasCaptions = fromPlayer.some((track) => !isOffCaptionTrack(track));
  const tracks = normalizeCaptionList(playerHasCaptions ? fromPlayer : tracksOverride?.length ? tracksOverride : fromPlayer);
  if (!tracks.length) {
    if (parsed.language === 'off') {
      setCaptions(0);
    }
    applyHtml5TextTracks(player, selected, root);
    return;
  }

  const index = findCaptionIndex(tracks, selected);
  if (index < 0) {
    setCaptions(0);
    applyHtml5TextTracks(player, selected, root);
    return;
  }

  setCaptions(index);
  applyHtml5TextTracks(player, selected, root);
};

export type ParsedCue = {
  start: number;
  end: number;
  text: string;
};

export type SidecarCaptionTrack = {
  id: string;
  language: string;
  label?: string;
  cues: ParsedCue[];
};

export type CaptionSource = {
  file?: string;
  kind?: string;
  label?: string;
  language?: string;
};

export const isThumbnailSpriteCue = (text: string) =>
  /#xywh=/i.test(text) || /\.(jpg|jpeg|png|webp)(#|\?|$)/i.test(text);

export const isCaptionSidecarFile = (file?: string, kind?: string) => {
  if (!file) {
    return false;
  }

  const normalizedKind = (kind || '').toLowerCase();
  if (normalizedKind === 'thumbnails' || normalizedKind === 'chapters' || normalizedKind === 'metadata') {
    return false;
  }

  if (/\/strips\/|thumbnails?|sprite/i.test(file)) {
    return false;
  }

  if (/\.(vtt|srt)($|\?)/i.test(file)) {
    return true;
  }

  return !normalizedKind || normalizedKind === 'captions' || normalizedKind === 'subtitles';
};

export const fixMojibake = (text: string) => {
  if (/[^\u0000-\u00FF]/.test(text)) {
    return text;
  }

  try {
    const encoded = Array.from(text, (char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join('');
    const decoded = decodeURIComponent(encoded);
    return decoded || text;
  } catch {
    return text;
  }
};

export const decodeVttBytes = (buffer: ArrayBuffer) => new TextDecoder('utf-8').decode(buffer);

export const resolveCaptionLanguage = (source: CaptionSource) =>
  canonicalLanguage(source.language) || labelLanguage(source.label) || '';

export const createCaptionSelections = (sources: CaptionSource[]) => {
  const languages = sources.map((source, index) => resolveCaptionLanguage(source) || `track${index}`);
  const baseLabels = sources.map((source, index) => {
    const language = languages[index];
    return source.label?.trim() || captionLanguageName(language) || language;
  });
  const languageCounts = languages.reduce<Record<string, number>>((acc, language) => {
    acc[language] = (acc[language] || 0) + 1;
    return acc;
  }, {});
  const labelCounts = baseLabels.reduce<Record<string, number>>((acc, label) => {
    const key = label.toLowerCase();
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const languageOrdinals: Record<string, number> = {};
  const labelOrdinals: Record<string, number> = {};

  return sources.map((source, index) => {
    const language = languages[index];
    const baseLabel = baseLabels[index];
    const labelKey = baseLabel.toLowerCase();
    const languageOrdinal = languageOrdinals[language] ?? 0;
    const labelOrdinal = labelOrdinals[labelKey] ?? 0;
    languageOrdinals[language] = languageOrdinal + 1;
    labelOrdinals[labelKey] = labelOrdinal + 1;

    return {
      id: languageCounts[language] > 1 ? `${language}:${languageOrdinal}` : language,
      language,
      label: labelCounts[labelKey] > 1 ? `${baseLabel} ${labelOrdinal + 1}` : baseLabel,
      file: source.file,
      source: 'sidecar' as const,
    };
  });
};

export type CaptionMenuSelection = {
  id: string;
  language: string;
  label: string;
  file?: string;
  jwIndex?: number;
  source: 'sidecar' | 'embedded';
};

/** Merge sideloaded VTT/SRT tracks with embedded m3u8 captions from JW.
 * JW also lists sideloaded tracks in getCaptionsList — skip those (exact label match)
 * so they are not shown again as "(Embedded)".
 */
export const mergeCaptionSelections = (
  sidecarSources: CaptionSource[],
  jwTracks: CaptionTrack[] = [],
): CaptionMenuSelection[] => {
  const sidecars = createCaptionSelections(sidecarSources);
  const list = normalizeCaptionList(jwTracks);
  const sidecarLabelsExact = new Set(sidecars.map((selection) => selection.label));
  const embeddedLanguages = new Set<string>();
  const embedded: CaptionMenuSelection[] = [];

  list.forEach((track, index) => {
    if (isOffCaptionTrack(track)) {
      return;
    }

    const language = trackLanguage(track) || `track${index}`;
    const baseLabel = track.label?.trim() || captionLanguageName(language) || `Track ${index}`;

    // Same label as a sideloaded track → JW is re-listing that file, not a new embedded track.
    if (sidecarLabelsExact.has(baseLabel)) {
      return;
    }

    // HLS often exposes both SUBTITLES and CLOSED-CAPTIONS for the same language.
    const languageKey = language.toLowerCase() || baseLabel.toLowerCase();
    if (embeddedLanguages.has(languageKey)) {
      return;
    }

    embeddedLanguages.add(languageKey);
    embedded.push({
      id: `embedded:${index}`,
      language,
      label: `${baseLabel} (Embedded)`,
      jwIndex: index,
      source: 'embedded',
    });
  });

  return [...sidecars, ...embedded];
};

export const buildSubtitleOptions = (selections: Array<{ id: string; label: string }>, offLabel: string) => [
  { label: offLabel, value: 'off' },
  ...selections.map((selection) => ({ label: selection.label, value: selection.id })),
];

export const hasCaptionTrackOptions = (options: Array<{ value: string }>) =>
  options.some((option) => option.value !== 'off');

export type AudioMenuSelection = {
  id: string;
  language: string;
  label: string;
  index: number;
};

export const resolveAudioTrackLanguage = (track: AudioTrack, index: number) =>
  canonicalLanguage(track.language) || labelLanguage(track.name || track.label) || `track${index}`;

export const createAudioTrackSelections = (tracks: AudioTrack[]): AudioMenuSelection[] => {
  if (!tracks.length) {
    return [];
  }

  const languages = tracks.map((track, index) => resolveAudioTrackLanguage(track, index));
  const baseLabels = tracks.map((track, index) => {
    const language = languages[index];
    return (track.name || track.label || '').trim() || captionLanguageName(language) || language;
  });
  const labelCounts = baseLabels.reduce<Record<string, number>>((acc, label) => {
    const key = label.toLowerCase();
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const labelOrdinals: Record<string, number> = {};

  return tracks.map((track, index) => {
    const language = languages[index];
    const baseLabel = baseLabels[index];
    const labelKey = baseLabel.toLowerCase();
    const labelOrdinal = labelOrdinals[labelKey] ?? 0;
    labelOrdinals[labelKey] = labelOrdinal + 1;

    return {
      id: toAudioTrackSelectionId(index),
      language,
      label: labelCounts[labelKey] > 1 ? `${baseLabel} ${labelOrdinal + 1}` : baseLabel,
      index,
    };
  });
};

export const buildAudioTrackOptions = (selections: AudioMenuSelection[]) =>
  selections.map((selection) => ({ label: selection.label, value: selection.id }));

export const hasAudioTrackOptions = (options: Array<{ value: string }>) => options.length > 1;

export const getSwitchableAudioTrackCount = (player?: CaptionsCapablePlayer) =>
  player?.getAudioTracks?.()?.length ?? 0;

export const getSwitchableAudioTracks = (player?: CaptionsCapablePlayer) => {
  const liveTracks = player?.getAudioTracks?.() ?? [];
  if (liveTracks.length > 1) {
    return enrichTracksWithHlsIndex(player, liveTracks);
  }

  const hlsTracks = getHlsJsAudioTracks(player);
  return hlsTracks.length > 1 ? hlsTracks : [];
};

const enrichTracksWithHlsIndex = (player: CaptionsCapablePlayer | undefined, tracks: AudioTrack[]) => {
  const hlsTracks = getHlsJsAudioTracks(player);
  if (!hlsTracks.length) {
    return tracks.map((track, index) => ({ ...track, hlsjsIndex: index }));
  }

  return tracks.map((track, index) => {
    const hlsMatch = hlsTracks.find(
      (hlsTrack) =>
        normalizeTrackLabel(hlsTrack) === normalizeTrackLabel(track) ||
        trackLanguage(hlsTrack) === trackLanguage(track),
    );

    return {
      ...track,
      hlsjsIndex: hlsMatch?.hlsjsIndex ?? index,
    };
  });
};

export const getAudioTrackSelectionId = (tracks: AudioTrack[], currentIndex: number) => {
  if (currentIndex < 0 || currentIndex >= tracks.length) {
    return tracks.length ? toAudioTrackSelectionId(0) : '';
  }

  return toAudioTrackSelectionId(currentIndex);
};

const parseHlsMediaAttributes = (line: string) => {
  const attributes: Record<string, string> = {};
  const payload = line.replace(/^#EXT-X-[^:]+:/i, '');
  const pattern = /([A-Z0-9-]+)=("([^"]*)"|([^,]*))/gi;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(payload))) {
    attributes[match[1].toUpperCase()] = match[3] ?? match[4] ?? '';
  }

  return attributes;
};

export const parseHlsAudioTracksFromManifest = (manifest: string): AudioTrack[] => {
  const tracks: AudioTrack[] = [];

  manifest.split(/\r?\n/).forEach((rawLine) => {
    const line = rawLine.trim();
    if (!/^#EXT-X-MEDIA:/i.test(line)) {
      return;
    }

    const attributes = parseHlsMediaAttributes(line);
    if ((attributes.TYPE || '').toUpperCase() !== 'AUDIO') {
      return;
    }

    tracks.push({
      name: attributes.NAME,
      language: attributes.LANGUAGE,
      groupid: attributes['GROUP-ID'],
      defaulttrack: (attributes.DEFAULT || '').toUpperCase() === 'YES',
      autoselect: (attributes.AUTOSELECT || '').toUpperCase() === 'YES',
      uri: attributes.URI || undefined,
    });
  });

  return tracks;
};

const resolveAbsoluteUrl = (baseUrl: string, relativeUrl: string) => {
  try {
    return new URL(relativeUrl, baseUrl).toString();
  } catch {
    return relativeUrl;
  }
};

export const parseHlsPlaylistDuration = (manifest: string): number | undefined => {
  let total = 0;
  let hasInf = false;

  manifest.split(/\r?\n/).forEach((rawLine) => {
    const match = rawLine.trim().match(/^#EXTINF:([\d.]+)/i);
    if (!match) {
      return;
    }

    total += Number(match[1]);
    hasInf = true;
  });

  return hasInf ? total : undefined;
};

export const fetchHlsPlaylistDuration = async (sourceUrl?: string): Promise<number | undefined> => {
  if (!sourceUrl || !/\.m3u8/i.test(sourceUrl)) {
    return undefined;
  }

  try {
    const response = await fetch(sourceUrl);
    if (!response.ok) {
      return undefined;
    }

    return parseHlsPlaylistDuration(await response.text());
  } catch {
    return undefined;
  }
};

export const resolveHlsPlaylistUrl = (...candidates: Array<string | undefined>) => {
  for (const candidate of candidates) {
    if (candidate && /\.m3u8/i.test(candidate)) {
      return candidate;
    }
  }

  return undefined;
};

export const fetchHlsAudioTracks = async (sourceUrl?: string): Promise<AudioTrack[]> => {
  if (!sourceUrl || !/\.m3u8/i.test(sourceUrl)) {
    return [];
  }

  try {
    const response = await fetch(sourceUrl);
    if (!response.ok) {
      return [];
    }

    return parseHlsAudioTracksFromManifest(await response.text()).map((track) => ({
      ...track,
      uri: track.uri ? resolveAbsoluteUrl(sourceUrl, track.uri) : undefined,
    }));
  } catch {
    return [];
  }
};

export const enrichAudioTracksWithManifestUris = async (
  tracks: AudioTrack[],
  ...sourceCandidates: Array<string | undefined>
): Promise<AudioTrack[]> => {
  if (tracks.length <= 1) {
    return tracks;
  }

  const hlsUrl = resolveHlsPlaylistUrl(...sourceCandidates);
  if (!hlsUrl) {
    return tracks;
  }

  const manifestTracks = await fetchHlsAudioTracks(hlsUrl);
  if (!manifestTracks.length) {
    return tracks;
  }

  return tracks.map((track, index) => {
    const byName = manifestTracks.find((item) => normalizeTrackLabel(item) === normalizeTrackLabel(track));
    const byLanguage = manifestTracks.find((item) => trackLanguage(item) === trackLanguage(track));
    const match = byName ?? byLanguage ?? manifestTracks[index];

    return {
      ...track,
      uri: track.uri || match?.uri,
      duration: track.duration ?? match?.duration,
    };
  });
};

export const ensureAudioTrackDuration = async (track?: AudioTrack): Promise<number | undefined> => {
  if (!track) {
    return undefined;
  }

  if (typeof track.duration === 'number' && Number.isFinite(track.duration) && track.duration > 0) {
    return track.duration;
  }

  if (!track.uri) {
    return undefined;
  }

  const duration = await fetchHlsPlaylistDuration(track.uri);
  if (typeof duration === 'number' && duration > 0) {
    track.duration = duration;
  }

  return duration;
};

export const getSelectedAudioTrack = (tracks: AudioTrack[], selected: string) => {
  const index = findAudioTrackIndex(tracks, selected);
  return index >= 0 ? tracks[index] : undefined;
};

export type AudioVideoDurationGuardState = {
  hasMultipleAudioTracks: boolean;
  audioDuration?: number;
  videoDuration: number;
  position: number;
  userMuted: boolean;
  audioEndedMute: boolean;
  playerState?: string;
};

export type AudioVideoDurationGuardResult = {
  audioEndedMute: boolean;
  mute?: boolean;
  unmute?: boolean;
  pause?: boolean;
  resumePlay?: boolean;
};

/** Keep playback aligned when alternate audio duration differs from video. */
export const evaluateAudioVideoDurationGuard = (
  state: AudioVideoDurationGuardState,
): AudioVideoDurationGuardResult => {
  const {
    hasMultipleAudioTracks,
    audioDuration,
    videoDuration,
    position,
    userMuted,
    audioEndedMute,
    playerState,
  } = state;

  if (!hasMultipleAudioTracks || !Number.isFinite(videoDuration) || videoDuration <= 0) {
    return { audioEndedMute: false };
  }

  // Audio longer than video: once video ends, stop residual audio.
  if (position >= videoDuration - 0.05) {
    return {
      audioEndedMute: false,
      pause: true,
      mute: true,
    };
  }

  if (typeof audioDuration !== 'number' || !Number.isFinite(audioDuration) || audioDuration <= 0) {
    return { audioEndedMute };
  }

  // Audio shorter than video: mute after audio ends so video can keep playing.
  if (audioDuration + 0.2 < videoDuration && position >= audioDuration - 0.05) {
    return {
      audioEndedMute: true,
      mute: userMuted ? undefined : true,
      resumePlay: playerState === 'buffering',
    };
  }

  if (audioEndedMute && position < audioDuration - 0.2) {
    return {
      audioEndedMute: false,
      unmute: userMuted ? undefined : true,
    };
  }

  return { audioEndedMute };
};

const isHlsJsLike = (value: unknown): value is HlsJsLike =>
  Boolean(
    value &&
      typeof value === 'object' &&
      ('audioTrack' in value ||
        (Array.isArray((value as HlsJsLike).audioTracks) && Boolean((value as HlsJsLike).audioTracks?.length))),
  );

const getHlsInstance = (player: ExtendedAudioPlayer, root?: HTMLElement | null): HlsJsLike | undefined => {
  const shallow = findShallowHlsInstance(player, root);
  if (shallow) {
    return shallow;
  }

  return deepFindHlsInstance(player, root);
};

const findShallowHlsInstance = (player: ExtendedAudioPlayer, root?: HTMLElement | null): HlsJsLike | undefined => {
  const candidates: unknown[] = [player.hls];

  const provider = player.getProvider?.();
  if (provider && typeof provider === 'object') {
    const providerRecord = provider as Record<string, unknown>;
    candidates.push(providerRecord.hls, providerRecord.hlsjs, providerRecord._hls, providerRecord._hlsjs);
    Object.values(providerRecord).forEach((value) => candidates.push(value));
  }

  const video = getPlayerVideo(player, root) as (HTMLVideoElement & { hls?: HlsJsLike; _hls?: HlsJsLike }) | null;
  candidates.push(video?.hls, video?._hls);

  for (const candidate of candidates) {
    if (isHlsJsLike(candidate)) {
      return candidate;
    }

    if (candidate && typeof candidate === 'object' && isHlsJsLike((candidate as { hls?: HlsJsLike }).hls)) {
      return (candidate as { hls: HlsJsLike }).hls;
    }
  }

  return undefined;
};

const deepFindHlsInstance = (player: ExtendedAudioPlayer, root?: HTMLElement | null): HlsJsLike | undefined => {
  const seen = new WeakSet<object>();

  const search = (value: unknown, depth = 0): HlsJsLike | undefined => {
    if (!value || typeof value !== 'object' || depth > 7) {
      return undefined;
    }

    if (seen.has(value as object)) {
      return undefined;
    }

    seen.add(value as object);

    if (isHlsJsLike(value)) {
      return value as HlsJsLike;
    }

    for (const nested of Object.values(value as object)) {
      if (isHlsJsLike(nested)) {
        return nested as HlsJsLike;
      }

      if (nested && typeof nested === 'object') {
        const found = search(nested, depth + 1);
        if (found) {
          return found;
        }
      }
    }

    return undefined;
  };

  return (
    search(player) ??
    search(player.getProvider?.()) ??
    search(getPlayerVideo(player, root)) ??
    search(player.getContainer?.())
  );
};

const getJwPlayerRoot = (player: CaptionsCapablePlayer) => {
  const container = player.getContainer?.();
  return (container?.closest('.jwplayer') ?? container) as HTMLElement | null | undefined;
};

const resolveJwPlayerInstance = (player: CaptionsCapablePlayer): CaptionsCapablePlayer => {
  const root = getJwPlayerRoot(player);
  const id = root?.id;

  if (typeof window !== 'undefined' && window.jwplayer && id) {
    try {
      return window.jwplayer(id) as CaptionsCapablePlayer;
    } catch {
      return player;
    }
  }

  return player;
};

export const getHlsJsAudioTracks = (player?: CaptionsCapablePlayer): AudioTrack[] => {
  if (!player) {
    return [];
  }

  const hls = getHlsInstance(player as ExtendedAudioPlayer);
  if (!hls?.audioTracks?.length) {
    return [];
  }

  return hls.audioTracks.map((track, index) => ({
    name: track.name,
    language: track.lang,
    groupid: track.groupId,
    hlsjsIndex: index,
  }));
};

export const collectAudioTracksFromPlayer = (player?: CaptionsCapablePlayer): AudioTrack[] => {
  const liveTracks = player?.getAudioTracks?.() ?? [];
  if (liveTracks.length > 1) {
    return enrichTracksWithHlsIndex(player, liveTracks);
  }

  const hlsTracks = getHlsJsAudioTracks(player);
  if (hlsTracks.length > liveTracks.length) {
    return hlsTracks;
  }

  return liveTracks;
};

const callJwSetCurrentAudioTrack = (player: CaptionsCapablePlayer, index: number) => {
  const setTrack =
    player.setCurrentAudioTrack ??
    (player as unknown as { setCurrentAudioTrack?: (trackIndex: number) => void }).setCurrentAudioTrack;

  if (typeof setTrack !== 'function') {
    return false;
  }

  setTrack.call(player, index);
  return true;
};

const parseTimestamp = (value: string) => {
  const normalized = value.trim().replace(',', '.');
  const parts = normalized.split(':');
  if (parts.length === 3) {
    return Number(parts[0]) * 3600 + Number(parts[1]) * 60 + Number(parts[2]);
  }
  if (parts.length === 2) {
    return Number(parts[0]) * 60 + Number(parts[1]);
  }
  return Number(normalized) || 0;
};

export const parseVtt = (vtt: string): ParsedCue[] => {
  const cues: ParsedCue[] = [];
  const blocks = vtt.replace(/^\uFEFF/, '').split(/\r?\n\r?\n/);

  blocks.forEach((block) => {
    const lines = block.split(/\r?\n/).filter((line) => line.trim() !== '' && line.trim() !== 'WEBVTT' && !line.startsWith('NOTE'));
    const timeLineIndex = lines.findIndex((line) => line.includes('-->'));
    if (timeLineIndex < 0) {
      return;
    }

    const [startRaw, endRaw] = lines[timeLineIndex].split('-->');
    const start = parseTimestamp(startRaw.replace(/ .*$/, ''));
    const end = parseTimestamp(endRaw.trim().split(/[\s]/)[0]);
    const text = fixMojibake(stripVttMarkup(lines.slice(timeLineIndex + 1).join('\n')));
    if (text && !isThumbnailSpriteCue(text)) {
      cues.push({ start, end, text });
    }
  });

  return cues;
};

/** SRT uses the same cue blocks as VTT with comma timestamps (already handled by parseTimestamp). */
export const parseSrt = (srt: string): ParsedCue[] => parseVtt(srt.replace(/^\uFEFF/, ''));

export const parseCaptionFile = (content: string, file?: string): ParsedCue[] => {
  if (file && /\.srt($|\?)/i.test(file)) {
    return parseSrt(content);
  }

  if (/^\s*WEBVTT/i.test(content)) {
    return parseVtt(content);
  }

  // JW may serve caption tracks without a clear extension; try VTT first, then SRT-style blocks.
  const vttCues = parseVtt(content);
  if (vttCues.length) {
    return vttCues;
  }

  return parseSrt(content);
};

export const collectCaptionTrackSources = (...groups: Array<CaptionSource[] | undefined>) => {
  const seen = new Set<string>();
  const sources: CaptionSource[] = [];

  groups.forEach((group) => {
    group?.forEach((track) => {
      if (!isCaptionSidecarFile(track.file, track.kind)) {
        return;
      }

      if (seen.has(track.file)) {
        return;
      }

      seen.add(track.file);
      sources.push(track);
    });
  });

  return sources;
};

export const loadCaptionSidecars = async (sources: CaptionSource[]): Promise<SidecarCaptionTrack[]> => {
  const selections = createCaptionSelections(sources);
  const loaded = await Promise.all(
    selections.map(async (selection) => {
      if (!selection.file) {
        return { ...selection, cues: [] };
      }

      try {
        const response = await fetch(selection.file);
        if (!response.ok) {
          return { ...selection, cues: [] };
        }

        const cues = parseCaptionFile(decodeVttBytes(await response.arrayBuffer()), selection.file);
        return {
          ...selection,
          cues,
        };
      } catch {
        return { ...selection, cues: [] };
      }
    }),
  );

  return loaded;
};

export const getSidecarCueText = (sidecars: SidecarCaptionTrack[], selected: string, time: number) => {
  if (selected === 'off' || parseCaptionSelection(selected).language === 'off' || !sidecars.length) {
    return '';
  }

  const track = sidecars.find((item) => item.id === selected);
  if (!track) {
    return '';
  }

  return track.cues
    .filter((cue) => time >= cue.start && time <= cue.end)
    .map((cue) => cue.text)
    .filter((text, index, lines) => lines.indexOf(text) === index)
    .join('\n');
};

export const applyAudioTrackToPlayer = (
  player: CaptionsCapablePlayer | undefined,
  selected: string,
  tracksOverride?: AudioTrack[],
  force = false,
) => {
  if (!player) {
    return;
  }

  const jwPlayer = resolveJwPlayerInstance(player);
  const liveTracks = getSwitchableAudioTracks(jwPlayer);
  const referenceTracks = liveTracks.length ? liveTracks : tracksOverride ?? jwPlayer.getAudioTracks?.() ?? [];
  if (!referenceTracks.length) {
    return;
  }

  const liveIndex = resolveLiveAudioTrackIndex(jwPlayer, selected, referenceTracks);
  if (liveIndex < 0) {
    return;
  }

  const currentIndex = jwPlayer.getCurrentAudioTrack?.();
  if (!force && typeof currentIndex === 'number' && currentIndex === liveIndex) {
    return;
  }

  callJwSetCurrentAudioTrack(jwPlayer, liveIndex);
};
